/**
 * Opened and Clicked, as two tiles inside "How your sends are doing".
 *
 * WHY THEY ARE HERE AND NOT ON A CARD OF THEIR OWN
 * -----------------------------------------------------------------------------
 * They answer the question the card's title asks. `EngagementCard` — parked,
 * intact, still asserted — answered it on a card of its own with an ISP
 * breakdown beside it; that card was taken off the page in slice 36 and, as a
 * side effect nobody intended, **no workspace has been able to see its own open
 * or click rate anywhere in Wend since**. This row puts the two figures back
 * where a reader is already looking, and leaves the ISP half parked.
 *
 * THE RANK OF THESE FIGURES IS DELIBERATE
 * -----------------------------------------------------------------------------
 * `1.5rem`, one rank below the `2rem` counts above and the deliverability
 * figure below. An open rate is *directional* — the contract says so in a
 * literal, `confidence: 'directional'`, and refuses to give it a threshold or a
 * band — so it must not be drawn at the same weight as a figure Amazon judges
 * an account by. The `Estimated` chip says the same thing in a word.
 *
 * THE BAR IS `neutral`, AND THAT IS THE WHOLE ARGUMENT
 * -----------------------------------------------------------------------------
 * Never `good`/`warn`/`bad`: those are verdicts, and a verdict needs a line to
 * be judged against. There is no open-rate line — nothing happens at 22% that
 * does not happen at 21% — which is why `engagementRateSchema` carries no
 * threshold and is not assignable to `OverviewRateKpi`.
 *
 * Never `info` either, which is this page's tone for a *projection*. These are
 * measured; they are only approximate. Drawing them in the projection's colour
 * would make an assumed figure and a measured one look alike, and telling those
 * two apart is most of what this screen is for.
 *
 * TWO FLOORS, TWO QUESTIONS
 * -----------------------------------------------------------------------------
 * This file uses `ENGAGEMENT_MIN_VOLUME_FOR_RATE` (100) and never the
 * Overview's `OVERVIEW_MIN_VOLUME_FOR_RATE` (1,000). The contract argues it:
 * the Overview's thousand falls out of AWS's 0.1% complaint threshold, where
 * being wrong costs an account. An open rate has no line, so its floor is only
 * asking *is this noise*. One number answering both questions would be wrong
 * for one of them.
 *
 * WHAT NEVER REACHES A BAR
 * -----------------------------------------------------------------------------
 * `metric.total` is every event row. `metric.unique` is distinct messages, and
 * it is the only numerator a rate or a bar may use. One person opening a
 * newsletter six times on the train did not open it six times.
 */

import {
  ENGAGEMENT_MIN_VOLUME_FOR_RATE,
  ENGAGEMENT_UNAVAILABLE_MESSAGES,
  ENGAGEMENT_CAVEAT_VALUES,
  type EngagementCaveat,
  type EngagementMetric,
  type EngagementPanel,
  type EngagementResponse,
  type EngagementUnavailable,
} from '@wend/shared';
import { Kicker, NoFigure, ProportionBar, Spinner, StateChip, cn } from '@wend/ui';
import { Link } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { MoreDetail } from '../templates/MoreDetail';
import { formatCount, formatDateTime } from '../../lib/format';
import type { AsyncData } from '../../lib/use-async-data';
import { integrationTabPath } from '../integrations/integration-tab-url';
import { formatRate } from './overview-format';
import { figureRole, TILE_SURFACE } from './OverviewFigures';
import {
  CaveatList,
  ENGAGEMENT_METRIC_COPY,
  ENGAGEMENT_SWITCH_REASONS,
  absentReasonFor,
} from './engagement-copy';

/* The rate tiles' skin (slice 66, `TILE_SURFACE` in OverviewFigures), so the
   engagement tiles sit in the same row as the same object. Their figures stay
   a rank smaller — 1.5rem against the bounce rate's 2rem — because opens and
   clicks must never read at the rank of the two rates that can cost somebody
   their AWS account; only the type role is shared, not the size. */
const TILE = cn('flex min-w-0 flex-1 basis-[13rem] flex-col gap-2 rounded-base border p-4', TILE_SURFACE.plain);

/** Which chip a missing half wears. Never a bare dash — silence reads as zero. */
const ABSENT_CHIP: Readonly<Record<EngagementUnavailable, { state: 'not_measured' | 'awaiting'; label?: string }>> = {
  tracking_off: { state: 'not_measured', label: 'Tracking off' },
  open_tracking_off: { state: 'not_measured', label: 'Tracking off' },
  click_tracking_off: { state: 'not_measured', label: 'Tracking off' },
  awaiting_first_events: { state: 'awaiting' },
  no_delivered_messages: { state: 'not_measured' },
  no_delivery_reports: { state: 'not_measured' },
};

/** Ordered by the contract's own list, so the order cannot follow insertion. */
function mergedCaveats(panel: Extract<EngagementPanel, { tracked: true }>): readonly EngagementCaveat[] {
  const seen = new Set<EngagementCaveat>();
  for (const metric of [panel.open, panel.click]) {
    for (const caveat of metric?.rate.caveats ?? []) seen.add(caveat);
  }
  return ENGAGEMENT_CAVEAT_VALUES.filter((caveat) => seen.has(caveat));
}

function MetricTile({
  kind,
  metric,
  unavailable,
}: {
  kind: 'open' | 'click';
  metric: EngagementMetric | null;
  unavailable: readonly EngagementUnavailable[];
}) {
  const copy = ENGAGEMENT_METRIC_COPY[kind];

  if (metric === null) {
    const reason = absentReasonFor(kind, unavailable);
    const chip = reason === null ? { state: 'not_measured' as const } : ABSENT_CHIP[reason];
    return (
      <div data-role="engagement-tile" data-kind={kind} className={TILE}>
        <Kicker as="h3">{copy.label}</Kicker>
        <NoFigure state={chip.state} className="text-[1.5rem] leading-none" />
        <StateChip state={chip.state} {...(chip.label === undefined ? {} : { label: chip.label })} />
        {/* The chip says which absence this is; the sentence explaining it is
            folded, per the page's no-extra-text rule. It is still in the DOM
            and still searchable. */}
        {reason !== null && (
          <MoreDetail summary="Why">
            <p>{ENGAGEMENT_UNAVAILABLE_MESSAGES[reason]}</p>
          </MoreDetail>
        )}
        {reason !== null && ENGAGEMENT_SWITCH_REASONS.includes(reason) && (
          <Link
            to={integrationTabPath('email')}
            className="self-start text-[0.78rem] text-text underline underline-offset-2"
          >
            Turn on tracking
          </Link>
        )}
      </div>
    );
  }

  const { reading } = metric.rate;
  const overFloor = reading.rate !== null && reading.denominator >= ENGAGEMENT_MIN_VOLUME_FOR_RATE;

  return (
    <div data-role="engagement-tile" data-kind={kind} className={TILE}>
      <Kicker as="h3">{copy.label}</Kicker>

      {overFloor ? (
        <>
          <span className={cn(figureRole('text-[1.5rem]'), 'text-text')}>
            {formatRate(reading.rate ?? 0)}
          </span>
          <StateChip state="estimated" />
          {/* `unique`, never `total` — see the header. */}
          <ProportionBar tone="neutral" value={reading.numerator} total={reading.denominator} />
          <span className="text-[0.72rem] tabular-nums text-muted">
            of {formatCount(reading.denominator)} delivered
          </span>
        </>
      ) : (
        <>
          {/*
            UNDER THE FLOOR: COUNTS, AND NO TRACK AT ALL.
            A bar filled three tenths of the way is a percentage a reader can
            read off it, and this state exists precisely to refuse one.
          */}
          <span className={cn(figureRole('text-[1.35rem]'), 'text-text')}>
            {formatCount(reading.numerator)}
          </span>
          <StateChip state="under_floor" />
          <span className="text-[0.72rem] tabular-nums text-muted">
            of {formatCount(reading.denominator)} delivered
          </span>
        </>
      )}
    </div>
  );
}

export function EngagementTiles({ engagement }: { engagement: AsyncData<EngagementResponse> }) {
  const { data, error, status, loadingFirst, reload } = engagement;

  if (loadingFirst) {
    return (
      <div data-role="engagement-counts" className="flex items-center gap-3 py-6 text-[0.85rem] text-muted">
        <Spinner size="sm" label={null} />
        <span role="status">Loading opens and clicks…</span>
      </div>
    );
  }

  if (status === 'error' && data === null) {
    return (
      <div data-role="engagement-counts" className="flex flex-col gap-2">
        <ErrorState
          error={error}
          announce={false}
          title="Opens and clicks did not load"
          onRetry={reload}
          className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
        />
      </div>
    );
  }

  if (data === null) return null;
  const panel = data.engagement;

  /*
   * TRACKING OFF: ONE TILE, NOT TWO, AND THE SENTENCE COMES OFF THE WIRE.
   *
   * One because the disclosure is counted exactly once on this page. Off the
   * wire because `tracking_off`'s sentence depends on whether this deployment
   * hosts pictures — `engagementTrackingOff(imageDelivery)` picks it — and the
   * browser does not have that fact. Reading the constant directly here would
   * print the embedded-pictures wording on a deployment that hosts them.
   */
  if (!panel.tracked) {
    return (
      <div data-role="engagement-counts" className="flex flex-col gap-3">
        <div data-role="engagement-tile" data-kind="both" className={TILE}>
          <Kicker as="h3">Opened and clicked</Kicker>
          <NoFigure state="not_measured" className="text-[1.5rem] leading-none" />
          <StateChip state="not_measured" label="Tracking off" />
          <MoreDetail summary="Why">
            <p>{panel.message}</p>
          </MoreDetail>
          {ENGAGEMENT_SWITCH_REASONS.includes(panel.reason) && (
            <Link
              to={integrationTabPath('email')}
              className="self-start text-[0.78rem] text-text underline underline-offset-2"
            >
              Turn on tracking
            </Link>
          )}
        </div>
      </div>
    );
  }

  const caveats = mergedCaveats(panel);
  const floorRefused =
    (panel.open !== null && panel.open.rate.reading.rate === null) ||
    (panel.click !== null && panel.click.rate.reading.rate === null);

  return (
    <div data-role="engagement-counts" className="flex flex-col gap-3">
      {status === 'error' && (
        <ErrorState
          error={error}
          announce={false}
          title="Could not refresh opens and clicks"
          onRetry={reload}
          className="flex flex-col items-start gap-2 rounded-base border border-line px-4 py-3"
        />
      )}

      <div className="flex flex-wrap gap-3">
        <MetricTile kind="open" metric={panel.open} unavailable={panel.unavailable} />
        <MetricTile kind="click" metric={panel.click} unavailable={panel.unavailable} />
      </div>

      {/*
        EVERYTHING EXPLANATORY IS FOLDED AWAY, AND THAT IS A RULE NOW.
        ---------------------------------------------------------------------
        The workspace owner's instruction: no extra text on the page, no
        textual presentation of a data point, explanation kept in the code for
        whoever needs it next. So the caveats, the denominator note and the
        read stamp are behind `MoreDetail` rather than printed under the
        figures.

        **They are folded, not deleted, and the difference is the whole point
        of using a disclosure here.** An open rate is wrong in two opposite
        directions at once — image blocking undercounts it, Apple's Mail
        Privacy Protection inflates it — and a reader who acts on 30.1% without
        ever being able to find that out has been misled by a screen that was
        trying to look clean. `<details>` keeps the children in the DOM while
        closed, so Ctrl+F still finds them, and `visibleBlocks` strips the
        whole block from the page's word budget. Short by default, detail on
        demand, nothing lost.
      */}
      <MoreDetail summary="What these figures include">
        <CaveatList caveats={caveats} />

        {floorRefused && (
          <p>
            Shown as counts rather than a percentage. Under{' '}
            {formatCount(ENGAGEMENT_MIN_VOLUME_FOR_RATE)} delivered messages a rate this
            approximate flips on a handful of mail apps, and the counts are true at any volume.
          </p>
        )}

        <p>
          Each message counts once, however many times it was opened or a link in it was
          followed.
        </p>
        <p>
          Measured over {formatCount(panel.delivered)} delivered messages — delivered, not
          accepted, because a message that bounced could not have been opened.
        </p>
        <p>Read at {formatDateTime(data.generatedAt)}.</p>
      </MoreDetail>
    </div>
  );
}
